/**
 * Ce que l'étape « Confirme ton absence » dit des crédits d'absence du masteur — les phrases de
 * l'ancien `ManageRDVScreen` (le bloc sous `other_occurrences`), dans ses quatre cas :
 *  - un élève change de masteur : une ligne discrète sous le bloc d'impact — aucun crédit si la
 *    séance se vide, le dépassement en rouge, sinon le décompte ;
 *  - sinon, au-delà de la limite : l'encadré « Attention » (perte des séances) ;
 *  - sinon : l'encadré « Important » (prévenir 24 h avant, le décompte, les frais de gestion).
 * Et, s'il y a lieu, le mois prochain.
 *
 * Pur, à côté de la molécule qui le pose (comme `absenceQuotaMessage`).
 */
import { type UITutorAbsenceCredits } from "../../../ports/primary/ui/types/tutorLives"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"

type TutorAbsenceCreditsMessage =
    | { kind: "line"; tone: "muted" | "danger"; text: string; charter: boolean }
    | { kind: "card"; severity: "alert" | "warning"; title: string; lines: string[] }

export function tutorAbsenceCreditsMessage(
    credits: UITutorAbsenceCredits,
    options: { tutorChange: boolean; emptiesSession: boolean }
): { main: TutorAbsenceCreditsMessage; nextMonth: string | null } {
    const { totalDays, limit, isNewTutor } = credits
    const exceeds = totalDays > limit
    const period = isNewTutor === true ? "durant ton premier mois" : "ce mois-ci"

    const nextMonth = (() => {
        const total = credits.nextMonthTotalDays ?? 0
        if (total <= 0) return null
        const verb = credits.nextMonthAddsDays === true ? "auras aussi épuisé" : "as déjà épuisé"
        return total > limit
            ? `Tu ${verb} ${countLabel(total, "crédit")} d'absence le mois prochain.`
            : `Tu ${verb} ${countLabel(total, "crédit")} d'absence sur les ${limit} autorisés le mois prochain.`
    })()

    if (options.tutorChange) {
        if (options.emptiesSession) {
            return {
                main: {
                    kind: "line",
                    tone: "muted",
                    text: "Aucun crédit d'absence décompté : il ne reste plus d'élève à remplacer.",
                    charter: false,
                },
                nextMonth,
            }
        }
        return {
            main: exceeds
                ? {
                      kind: "line",
                      tone: "danger",
                      text: `Absences ${period} : ${countLabel(totalDays, "jour")}, au-delà de la limite — tu risques de perdre tes séances.`,
                      charter: true,
                  }
                : {
                      kind: "line",
                      tone: "muted",
                      text: `Crédits d'absence ce mois-ci : ${totalDays} sur ${limit}. Absences répétées ou tardives : frais de gestion.`,
                      charter: true,
                  },
            nextMonth,
        }
    }

    if (exceeds) {
        return {
            main: {
                kind: "card",
                severity: "alert",
                title: "Attention",
                lines: [
                    `En confirmant, tu auras été absent ${countLabel(totalDays, "jour")} ${isNewTutor === true ? "durant ton premier mois" : "du mois en cours"}. Tu risques de perdre tes séances conformément à la charte d'engagement.`,
                ],
            },
            nextMonth,
        }
    }
    return {
        main: {
            kind: "card",
            severity: "warning",
            title: "Important",
            lines: [
                "Préviens au moins 24 h à l'avance pour faciliter l'organisation de ton remplacement.",
                `En confirmant, tu auras épuisé ${countLabel(totalDays, "crédit")} d'absence sur les ${limit} autorisés du mois en cours. Des absences répétées ou tardives entraîneront des frais de gestion conformément à la charte d'engagement.`,
            ],
        },
        nextMonth,
    }
}
